import { Box } from '@chakra-ui/react';
import { BaseTooltip, useStandardToast } from '@cks-internal/react-base';
import React from 'react';
import { FC } from 'react';

type FlsqCopyBadgeProps = {
  flsq?: string | number | null;
  tooltipPrefix?: string;
  groupedFlsqs?: (string | number)[];
  isTransparent?: boolean;
  color?: string;
  fontWeight?: string;
  fontSize?: string;
};

/**
 * Displays a badge representing an FLSQ value, optionally grouped with related FLSQs.
 * Includes a tooltip for copying the primary FLSQ and optionally listing the group.
 *
 * @param {FlsqCopyBadgeProps} props - Props for the badge.
 * @returns {JSX.Element} A clickable badge or a placeholder label.
 */
export const FlsqCopyBadge: FC<FlsqCopyBadgeProps> = ({
  flsq,
  tooltipPrefix = 'Click to copy FLSQ',
  groupedFlsqs,
  isTransparent,
  color,
  fontWeight,
  fontSize
}) => {
  const value = flsq?.toString().trim() || '';
  const isCopyable = value !== '';
  const toast = useStandardToast();

  const copyToClipboard = () => {
    if (!isCopyable) return;
    navigator.clipboard.writeText(value);
    toast.successToast('Copied to clipboard', `${value} has been copied to your clipboard.`);
  };

  const renderGroupedFlsqs = () => {
    if (!groupedFlsqs || groupedFlsqs.length === 0) return null;

    const lines: React.ReactNode[] = [];
    for (let i = 0; i < groupedFlsqs.length; i += 5) {
      const slice = groupedFlsqs.slice(i, i + 5).map((id, idx) => (
        <React.Fragment key={i + idx}>
          {idx > 0 ? ', ' : ''}
          {id}
        </React.Fragment>
      ));
      lines.push(<div key={i}>{slice}</div>);
    }

    return <>{lines}</>;
  };

  return (
    <BaseTooltip
      content={
        <Box>
          <Box fontSize='sm' fontWeight='semibold'>
            {isCopyable ? `${tooltipPrefix}: ${value}` : 'No FLSQ available'}
          </Box>
          {groupedFlsqs && groupedFlsqs?.length > 0 && (
            <Box mt={1}>
              <Box>Grouped FLSQs:</Box>
              {renderGroupedFlsqs()}
            </Box>
          )}
        </Box>
      }>
      <Box
        display='inline-flex'
        alignItems='center'
        justifyContent='center'
        height='24px'
        borderRadius='md'
        bg={isTransparent ? 'transparent' : 'white'}
        pl={'0px'}
        ml={'0px'}
        mr={'4px'}
        border='1px solid'
        borderColor={isTransparent ? 'transparent' : 'white'}
        fontSize={fontSize || (isCopyable ? 'md' : 'sm')}
        fontWeight={fontWeight || 'bold'}
        color={color}
        textAlign='center'
        cursor={isCopyable ? 'pointer' : 'default'}
        opacity={isCopyable ? 1 : 0.6}
        onClick={copyToClipboard}
        role='button'
        aria-label={isCopyable ? `Copy FLSQ ${value}` : 'No FLSQ available'}>
        {isCopyable ? value : 'No Flsq'}
      </Box>
    </BaseTooltip>
  );
};
